import React, { useContext, useEffect, useState } from "react"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as errors from "errors"
import { CancellablePromise } from "real-cancellable-promise"

export const Context = React.createContext(zero())
export const Provider = Context.Provider

export function useCached(): cached {
	return useContext(Context)
}

export interface cached {
	documents: api.DocumentExtract[]
	currentDocument: api.DocumentExtract | null
	assets: api.AssetExtract[]
	selectedAssets: Set<string>
	undoStack: api.AssetExtract[]
	loading: boolean
	refreshDocuments: () => Promise<void>
	refreshAssets: (documentId: string) => Promise<void>
	setSelectedAssets: (assets: Set<string>) => void
	addToUndoStack: (asset: api.AssetExtract) => void
	removeFromUndoStack: (assetId: string) => void
}

export function zero(c: Partial<cached> = {}): cached {
	return {
		documents: [],
		currentDocument: null,
		assets: [],
		selectedAssets: new Set(),
		undoStack: [],
		loading: false,
		refreshDocuments: async () => {},
		refreshAssets: async () => {},
		setSelectedAssets: () => {},
		addToUndoStack: () => {},
		removeFromUndoStack: () => {},
		...c,
	}
}

export function Storage(props: React.PropsWithChildren<unknown>): JSX.Element {
	const brand = brands.caching.useCached()
	const metaauthz = authzc.useCache((cached) => cached.meta)
	const [documents, setDocuments] = useState<api.DocumentExtract[]>([])
	const [currentDocument, setCurrentDocument] = useState<api.DocumentExtract | null>(null)
	const [assets, setAssets] = useState<api.AssetExtract[]>([])
	const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set())
	const [undoStack, setUndoStack] = useState<api.AssetExtract[]>([])
	const [loading, setLoading] = useState(false)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const refreshDocuments = async (): Promise<void> => {
		const retry = httpx.autoretry()
		const req = api.zeros.documents.searchZero({ brand_id: brand.id })
		return retry
			.wrap(() => api.documents.search(brand.id, req, authzc.bearer(metaauthz)))
			.then((response) => {
				setDocuments(response.documents || [])
			})
			.catch(httpx.errors.cancellation(console.warn))
			.catch((e) => console.error("unable to load documents", e))
	}

	const refreshAssets = async (documentId: string): Promise<void> => {
		const retry = httpx.autoretry()
		const req = api.zeros.assets.searchZero({ document_id: documentId })
		return retry
			.wrap(() => api.assets.search(brand.id, documentId, req, authzc.bearer(metaauthz)))
			.then((response) => {
				setAssets(response.assets || [])
			})
			.catch(httpx.errors.cancellation(console.warn))
			.catch((e) => console.error("unable to load assets", e))
	}

	const addToUndoStack = (asset: api.AssetExtract): void => {
		setUndoStack((prev) => [...prev, asset])
	}

	const removeFromUndoStack = (assetId: string): void => {
		setUndoStack((prev) => prev.filter((a) => a.id !== assetId))
	}

	const cached: cached = {
		documents,
		currentDocument,
		assets,
		selectedAssets,
		undoStack,
		loading,
		refreshDocuments,
		refreshAssets,
		setSelectedAssets,
		addToUndoStack,
		removeFromUndoStack,
	}

	useEffect(() => {
		setLoading(true)
		refreshDocuments()
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view brand guide v2", cause)
					setCause(
						<layouts.Flex flex="1" alignItems="center" justifyContent="center">
							<errors.Textual textAlign="center" lineHeight="30px">
								You do not have permission to view BrandGuide Builder V2
								<br />
								You need to talk to your account administrator to check permissions.
							</errors.Textual>
						</layouts.Flex>,
					)
				}),
			)
			.finally(() => setLoading(false))
	}, [brand.id])

	return (
		<layouts.loading.pending loading={loading}>
			<Provider value={cached}>{cause ? cause : props.children}</Provider>
		</layouts.loading.pending>
	)
}
